import { useState, useEffect, createContext, useContext, useRef } from 'react';

// Define the dialogue timing interface
interface DialogueTiming {
  start: number;
  end: number;
}

// Define the context interface
interface DialogueHighlightContextType {
  currentDialogueIndex: number;
  setCurrentTime: (time: number) => void;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  setDialogueTimings: (timings: DialogueTiming[]) => void;
}

// Create the context with default values
const DialogueHighlightContext = createContext<DialogueHighlightContextType>({
  currentDialogueIndex: -1,
  setCurrentTime: () => {},
  isPlaying: false,
  setIsPlaying: () => {},
  setDialogueTimings: () => {},
});

// Provider component
export const DialogueHighlightProvider: React.FC<{
  children: React.ReactNode;
  dialoguesCount: number;
}> = ({ children, dialoguesCount }) => {
  const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0); // Start with first dialogue highlighted
  const [isPlaying, setIsPlaying] = useState(false);
  const dialogueTimingsRef = useRef<DialogueTiming[]>([]);
  
  // Function to set dialogue timings based on audio durations
  const setDialogueTimings = (timings: DialogueTiming[]) => {
    dialogueTimingsRef.current = timings;
  };
  
  // Function to calculate which dialogue should be active based on current time
  const setCurrentTime = (currentTimeInSeconds: number) => {
    // Simple fallback with even distribution if no proper timings
    if (dialogueTimingsRef.current.length === 0) {
      const DIALOGUE_AVG_DURATION = 8; // fallback duration
      const estimatedIndex = Math.min(
        Math.floor(currentTimeInSeconds / DIALOGUE_AVG_DURATION),
        dialoguesCount - 1
      );
      
      if (estimatedIndex !== currentDialogueIndex) {
        setCurrentDialogueIndex(estimatedIndex);
      }
      return;
    }
    
    // If we have precise timings, use them
    // Find which dialogue's time range contains the current time
    for (let i = 0; i < dialogueTimingsRef.current.length; i++) {
      const timing = dialogueTimingsRef.current[i];
      if (currentTimeInSeconds >= timing.start && currentTimeInSeconds < timing.end) {
        if (i !== currentDialogueIndex) {
          setCurrentDialogueIndex(i);
        }
        return;
      }
    }
    
    // If time is beyond all dialogues, highlight the last one
    if (currentTimeInSeconds >= dialogueTimingsRef.current[dialogueTimingsRef.current.length - 1].end) {
      const lastIndex = dialogueTimingsRef.current.length - 1;
      if (lastIndex !== currentDialogueIndex) {
        setCurrentDialogueIndex(lastIndex);
      }
    }
  };
  
  return (
    <DialogueHighlightContext.Provider
      value={{
        currentDialogueIndex,
        setCurrentTime,
        isPlaying,
        setIsPlaying,
        setDialogueTimings,
      }}
    >
      {children}
    </DialogueHighlightContext.Provider>
  );
};

// Custom hook to use the dialogue highlighting context
export const useDialogueHighlight = () => useContext(DialogueHighlightContext); 